import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable, from, of, Subject } from 'rxjs';
import { map, tap, catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { createRemoteJWKSet, jwtVerify } from 'jose';
import { CookieService } from 'ngx-cookie-service';
import { SilentAuthService } from './silent-auth.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly CODE_VERIFIER_KEY = 'code_verifier';
  private readonly CODE_CHALLENGE_KEY = 'code_challenge';
  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly EXPIRE_AT_KEY = 'expireAt';
  private readonly REDIRECT_AFTER_LOGIN_KEY = 'redirectAfterLogin';
  private readonly REDIRECT_AFTER_LOGIN_IGNORE = ['/', '/login', '/condominiuns'];

  private readonly JWKS = createRemoteJWKSet(new URL(`${environment.authServer}/oauth2/jwks`));

  private loginSubject = new Subject<void>();
  public login$ = this.loginSubject.asObservable();

  private tokenUpdatedSubject = new Subject<void>();
  public tokenUpdated$ = this.tokenUpdatedSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router
  ) { }

  private generateCodeVerifier(): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const verifierLength = 128;
    let verifier = '';

    const array = new Uint8Array(verifierLength);
    crypto.getRandomValues(array);

    for (let i = 0; i < verifierLength; i++) {
      verifier += charset[array[i] % charset.length];
    }

    return verifier;
  }

  private async generateCodeChallengeHash(verifier: string): Promise<string> {
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return this.base64URLEncode(new Uint8Array(hash));
  }

  private base64URLEncode(buffer: Uint8Array): string {
    return btoa(String.fromCharCode(...buffer))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  private storeInLocalStorage(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  private removeFromLocalStorage(key: string): void {
    localStorage.removeItem(key);
  }

  private getFromLocalStorage(key: string): string | null {
    return localStorage.getItem(key);
  }

  async initiateLogin(): Promise<void> {
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);

    this.storeInLocalStorage(this.CODE_VERIFIER_KEY, codeVerifier);
    this.storeInLocalStorage(this.CODE_CHALLENGE_KEY, codeChallenge);
    const authUrl = `${environment.authServer}/oauth2/authorize?` +
      `response_type=code` +
      `&client_id=public-client` +
      `&redirect_uri=${environment.redirectUri}` +
      `&scope=openid%20profile%20offline_access` +
      `&code_challenge=${codeChallenge}` +
      `&code_challenge_method=S256`;
    window.location.href = authUrl;
  }

  handleCallback(code: string): Observable<any> {
    const codeVerifier = this.getFromLocalStorage(this.CODE_VERIFIER_KEY);
    if (!codeVerifier) {
      throw new Error('Code verifier not found');
    }

    const body = new URLSearchParams();
    body.set('client_id', 'public-client');
    body.set('grant_type', 'authorization_code');
    body.set('redirect_uri', environment.redirectUri);
    body.set('code', code);
    body.set('code_verifier', codeVerifier);

    return this.http.post(`${environment.authServer}/oauth2/token`, body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }).pipe(
      tap((response: any) => {
        this.storeInLocalStorage(this.ACCESS_TOKEN_KEY, response.access_token);
        this.storeInLocalStorage(this.REFRESH_TOKEN_KEY, response.refresh_token);

        // Decode JWT to get expiration time
        const tokenPayload = JSON.parse(atob(response.access_token.split('.')[1]));
        const expireAt = new Date((tokenPayload.exp - 10) * 1000).toISOString();
        this.storeInLocalStorage('expireAt', expireAt);

        this.tokenUpdatedSubject.next();
        this.router.navigate(['/dashboard']);
        this.loginSubject.next();
      })
    );
  }

  private refreshAccessToken(): Observable<any> {
    const refreshToken = this.getFromLocalStorage(this.REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      return from([false]);
    }

    const body = new URLSearchParams();
    body.set('grant_type', 'refresh_token');
    body.set('refresh_token', refreshToken);
    body.set('client_id', environment.oauth2.authorizationCode.clientId);

    return this.http.post(`${environment.authServer}/oauth2/token`, body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }).pipe(
      tap((response: any) => {
        this.storeInLocalStorage(this.ACCESS_TOKEN_KEY, response.access_token);
        if (response.refresh_token) {
          this.storeInLocalStorage(this.REFRESH_TOKEN_KEY, response.refresh_token);
        }

        // Decode JWT to get expiration time
        const tokenPayload = JSON.parse(atob(response.access_token.split('.')[1]));
        const expireAt = new Date((tokenPayload.exp - 10) * 1000).toISOString();
        this.storeInLocalStorage('expireAt', expireAt);
        this.tokenUpdatedSubject.next();
      })
    );
  }

  private verifyTokenOffline(token: string): Promise<boolean> {
    return jwtVerify(token, this.JWKS, {
      issuer: environment.authServer, // ajuste se seu issuer for diferente
      audience: environment.oauth2.authorizationCode.clientId
    })
      .then(() => true)
      .catch(() => false);
  }

  isAuthenticated(): Observable<boolean> {
    const token = this.getFromLocalStorage(this.ACCESS_TOKEN_KEY);
    if (!token) {
      return of(false);
    }
    return from(this.verifyTokenOffline(token)).pipe(
      switchMap(valid => {
        if (valid) return of(true);
        return this.refreshAccessToken().pipe(
          map(() => true),
          catchError(() => {
            this.logout();
            return of(false);
          })
        );
      })
    );
  }

  /**
   * Reads the email_confirmed claim from the current access token payload.
   * Returns null when there is no token or the payload cannot be decoded.
   */
  getEmailConfirmedClaim(): boolean | null {
    const token = this.getFromLocalStorage(this.ACCESS_TOKEN_KEY);
    if (!token) {
      return null;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (typeof payload.email_confirmed === 'boolean') {
        return payload.email_confirmed;
      }
      if (payload.email_confirmed === 'true') {
        return true;
      }
      if (payload.email_confirmed === 'false') {
        return false;
      }
      return null;
    } catch {
      return null;
    }
  }

  public setRedirectAfterLogin(route: string): void {
    if (this.REDIRECT_AFTER_LOGIN_IGNORE.includes(route)) {
      return;
    }
    this.storeInLocalStorage(this.REDIRECT_AFTER_LOGIN_KEY, route);
  }

  public getRedirectAfterLogin(): string | null {
    return this.getFromLocalStorage(this.REDIRECT_AFTER_LOGIN_KEY);
  }

  public resetRedirectAfterLogin(): void {
    this.removeFromLocalStorage(this.REDIRECT_AFTER_LOGIN_KEY);
  }

  logout(): void {
    this.http.get(
      `${environment.authServer}/csrf`,
      { withCredentials: true }
    ).subscribe({
      next: () => {
        const csrfToken = this.getCookie('XSRF-TOKEN');
        // Encerra a sessão no authserver após limpar o storage local
        this.http.post(`${environment.authServer}/logout`, null, {
          withCredentials: true,
          headers: {
            'X-XSRF-TOKEN': csrfToken
          }
        }).subscribe({
          next: () => {
            // limpa storage local
            localStorage.removeItem(this.ACCESS_TOKEN_KEY);
            localStorage.removeItem(this.REFRESH_TOKEN_KEY);
            localStorage.removeItem(this.CODE_VERIFIER_KEY);
            localStorage.removeItem(this.CODE_CHALLENGE_KEY);
            localStorage.removeItem(this.EXPIRE_AT_KEY);

            window.location.href = '/';
          }
        });
      }
    });

  }

  private getCookie(name: string): string {
    const cookie = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${name}=`));

    return cookie
      ? decodeURIComponent(cookie.substring(name.length + 1))
      : '';
  }

  getClientCredentialsToken(scope: string): Observable<any> {
    const credentials = btoa(`${environment.oauth2.clientCredentials.clientId}:${environment.oauth2.clientCredentials.clientSecret}`);

    const body = new URLSearchParams();
    body.set('grant_type', 'client_credentials');
    body.set('scope', scope);

    return this.http.post(`${environment.authServer}/oauth2/token`, body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Basic ${credentials}`
      }
    });
  }

  getPKCEToken(): Observable<string> {
    const token = localStorage.getItem(this.ACCESS_TOKEN_KEY);
    const expireAt = localStorage.getItem(this.EXPIRE_AT_KEY);

    if (!token || !expireAt) {
      this.router.navigate(['/']);
      throw new Error('No token available');
    }

    const expireDate = new Date(expireAt);
    if (expireDate > new Date()) {
      return of(token);
    }

    return this.refreshAccessToken().pipe(
      map(response => {
        return response.access_token;
      }),
      catchError(error => {
        console.error('Token refresh failed:', error);
        this.router.navigate(['/']);
        throw new Error('Failed to refresh token');
      })
    );
  }

  public async generateNewCodeVerifier(): Promise<string> {
    const verifier = this.generateCodeVerifier();
    this.storeInLocalStorage(this.CODE_VERIFIER_KEY, verifier);
    return verifier;
  }

  public async generateCodeChallenge(verifier: string): Promise<string> {
    const challenge = await this.generateCodeChallengeHash(verifier);
    this.storeInLocalStorage(this.CODE_CHALLENGE_KEY, challenge);
    return challenge;
  }

  public handleSilentCallback(code: string, codeVerifier: string, redirectUri: string): Promise<string> {
    const body = new URLSearchParams();
    body.set('client_id', 'public-client');
    body.set('grant_type', 'authorization_code');
    body.set('redirect_uri', redirectUri);
    body.set('code', code);
    body.set('code_verifier', codeVerifier);

    return this.http.post(`${environment.authServer}/oauth2/token`, body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }).pipe(
      tap((response: any) => {
        this.storeInLocalStorage(this.ACCESS_TOKEN_KEY, response.access_token);

        // Decode JWT to get expiration time
        const tokenPayload = JSON.parse(atob(response.access_token.split('.')[1]));
        const expireAt = new Date((tokenPayload.exp - 10) * 1000).toISOString();
        this.storeInLocalStorage('expireAt', expireAt);
        this.tokenUpdatedSubject.next();
      }),
      map(response => response.access_token)
    ).toPromise();
  }
}
